import * as React from 'react';
import {Linking, Platform, View} from 'react-native';
import {Breadcrumbs} from '../../shared/components/breadcrumbs';
import {Markdown} from '../../shared/components/markdown';
import Screen from '../../shared/components/screen';
import {CustomScrollView} from '../../shared/components/scrollView';
import {Text} from '../../shared/components/text';
import {useStyles} from '../../shared/hooks/styles';
import {Content} from '../../shared/models/content';
import {Routes, Screens} from '../../shared/routing';
import {useTheme} from '../../shared/theme';
import {DeviceSize, maxDesktopContentWidth, useDeviceSize} from '../../shared/theme/helpers';
import {Image} from '../../shared/components/image';
import {Paper, makeStyles} from '@material-ui/core';
import {CASHBACK_FORM_URL} from 'react-native-dotenv';
import {ContentWithRightImage} from '../../shared/components/contentWithRightImage';
import {CashbackPng} from '../../assets/images';
import WebView, {WebViewMessageEvent} from 'react-native-webview';
import {LoadableContent} from '../../shared/components/loadableContent';
import {getUrlHostname} from '../../shared/utils/getUrlHostname';

interface Props {
  content: Content;
}

export default ({content}: Props) => {
  const routes = [
    {
      title: 'Возможности карты',
      routeName: Screens.CardServices,
      webRoute: Routes.cardService,
    },
    {
      title: 'Регистрация в акциях Программы лояльности АО «НСПК»',
      routeName: Screens.Cashback,
      webRoute: Routes.cashBack,
    },
  ];
  const cashBackFormUrl =
    CASHBACK_FORM_URL || 'https://form.vamprivet.ru/register-iframe/resident_card/SVERDLOVSK_URALOCHKA_CARD/';
  const residentCardIFrame = React.useRef<HTMLIFrameElement>(null);
  const theme = useTheme();
  const deviceSize = useDeviceSize();
  const [isLoading, setIsLoading] = React.useState(true);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [webViewHeight, setWebViewHeight] = React.useState(0);
  const isOldAndroid = Platform.OS === 'android' && typeof Platform.Version === 'number' && Platform.Version < 29;

  const frameStyles = makeStyles((t) => ({
    cashBackProgramFrame: {
      padding: 0,
      border: 0,
      width: isLoading ? 0 : '100%',
      height: isLoading ? '0 !important' : '40rem',
      [t.breakpoints.down(DeviceSize.tablet)]: {
        height: isLoading ? '0 !important' : '40rem',
        width: isLoading ? '0 !important' : 'auto',
      },
      [t.breakpoints.down(695)]: {
        height: isLoading ? '0 !important' : '45rem',
        width: isLoading ? '0 !important' : '100%',
        border: 0,
      },
    },
    cashBackFrameContainer: {
      padding: 0,
      marginTop: '2rem',
      marginBottom: '6rem',
      borderRadius: 14,
      height: '100%',
      width: '40rem',
      [t.breakpoints.down(695)]: {
        width: '100%',
        height: 'auto',
        alignSelf: 'center',
        minHeight: '42rem',
      },
      [t.breakpoints.down(992)]: {
        alignSelf: 'center',
      },
      ...(Platform.OS === 'ios' && {alignSelf: 'center'}),
      overflow: 'hidden !important',
    },
    cashBackProgramAgreementBox: {
      margin: '1rem',
    },
  }))();

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleWebViewError = (syntheticEvent: any) => {
    const {nativeEvent} = syntheticEvent;
    console.error('WebView error:', nativeEvent);
    setIsLoading(false);
  };

  const handleWebViewHttpError = (syntheticEvent: any) => {
    const {nativeEvent} = syntheticEvent;
    console.error('WebView HTTP error:', nativeEvent);
  };

  const styles = useStyles(({mediaOnly}) => {
    return {
      sectionContainer: {
        flexGrow: 1,
        flexDirection: 'column',
        alignItems: 'center',
        ...mediaOnly(DeviceSize.mobile, {paddingHorizontal: 16}, {}),
        ...mediaOnly(DeviceSize.tablet, {paddingHorizontal: 35}, {}),
        ...mediaOnly(DeviceSize.desktop, {paddingHorizontal: 60}, {}),
      },
      contentTextWrapper: {
        marginBottom: 0,
      },
      contentContainer: {
        width: '100%',
        maxWidth: maxDesktopContentWidth,
        flexDirection: 'column',
        alignItems: 'stretch',
      },
      programSectionContainer: {
        ...mediaOnly(DeviceSize.desktop, {marginTop: 32}, {marginTop: 20}),
        backgroundColor: theme.screen.container[deviceSize].backgroundColor,
      },
      programTitle: {
        ...theme.fonts.h2,
        marginBottom: 30,
        marginTop: 40,
        width: '100%',
        ...mediaOnly(DeviceSize.mobile, {...theme.fonts.h5}),
      },
      programContentWithImageContainer: {},
      programContentWithImageIcon: {
        marginTop: -65,
        marginRight: 35,
        width: 397,
        height: 352,
      },
      programContentWithImageBackground: {
        ...mediaOnly(DeviceSize.desktop, {top: -57, right: 54, width: 424, height: 436}, {}),
      },
      programContentWithImageContent: mediaOnly(DeviceSize.mobile, {minHeight: 1}, {minHeight: 'auto'}),
      webView: {
        borderRadius: 5,
        minHeight: 800,
      },
      cashBackProgramAgreementButtonBox: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
      },
    };
  });

  const onMessage = (e: WebViewMessageEvent) => {
    try {
      const rawData = e.nativeEvent.data;
      if (!rawData || typeof rawData !== 'string' || !rawData.startsWith('{')) {
        return;
      }
      const data = JSON.parse(rawData);
      if (data?.height) {
        const next = Math.max(1, Number(data.height) || 0);

        if (next !== webViewHeight) setWebViewHeight(next);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const INJECTED_JAVASCRIPT = `(function() {
    window.addEventListener('message',function(event){
        window.ReactNativeWebView.postMessage(event.data);
      },false);
    })();`;

  return (
    <Screen fullWidth>
      <CustomScrollView contentInsetAdjustmentBehavior='automatic'>
        <View style={[styles.sectionContainer, styles.programSectionContainer]}>
          <View style={styles.contentContainer}>
            <Breadcrumbs routes={routes} />
            <Text testID='baby-food-title-id' style={styles.programTitle}>
              {`Регистрация в акциях\nПрограммы лояльности АО «НСПК»`}
            </Text>
            <ContentWithRightImage
              hideImage={deviceSize !== DeviceSize.desktop}
              containerStyle={styles.programContentWithImageContainer}
              icon={<Image source={CashbackPng as any} width={397} height={352} />}
              iconStyle={styles.programContentWithImageIcon}
              backgroundStyle={styles.programContentWithImageBackground}
              contentStyle={styles.programContentWithImageContent}>
              <View style={styles.contentTextWrapper}>
                <Markdown listColumns={deviceSize === DeviceSize.desktop ? 2 : 1}>
                  {content.services.cashBack.descriptionMarkdown}
                </Markdown>
                {Platform.OS === 'web' && (
                  <Paper elevation={6} className={frameStyles.cashBackFrameContainer}>
                    {isLoading && <LoadableContent loading={isLoading} />}
                    <iframe
                      title='NSPKframe'
                      ref={residentCardIFrame}
                      onLoad={handleIframeLoad}
                      className={frameStyles.cashBackProgramFrame}
                      src={cashBackFormUrl}
                    />
                  </Paper>
                )}
                {Platform.OS !== 'web' && (
                  <WebView
                    domStorageEnabled
                    injectedJavaScript={INJECTED_JAVASCRIPT}
                    onMessage={onMessage}
                    mixedContentMode='compatibility'
                    source={{uri: cashBackFormUrl}}
                    containerStyle={styles.webView}
                    style={{height: webViewHeight}}
                    startInLoadingState
                    androidLayerType={isOldAndroid ? 'software' : undefined}
                    scrollEnabled={false}
                    overScrollMode={'never'}
                    onLoadEnd={() => {
                      setIsLoaded(true);
                      setIsLoading(false);
                    }}
                    onError={handleWebViewError}
                    onHttpError={handleWebViewHttpError}
                    onShouldStartLoadWithRequest={(request) => {
                      if (Platform.OS === 'android' || !isLoaded) return true;

                      const nextHostName = getUrlHostname(request.url);
                      const hostsToOpenExternally = ['www.nspk.ru', 'nspk.ru', 'privetmir.ru'];

                      const isNavigationRequest =
                        request.navigationType === 'click' ||
                        request.navigationType === 'formsubmit' ||
                        request.navigationType === 'formresubmit';

                      if (isNavigationRequest && nextHostName && hostsToOpenExternally.includes(nextHostName)) {
                        console.log('Opening external URL:', request.url);
                        Linking.openURL(request.url);
                        return false;
                      }
                      return true;
                    }}
                  />
                )}
              </View>
            </ContentWithRightImage>
          </View>
        </View>
      </CustomScrollView>
    </Screen>
  );
};
